// src/pages/user/Authors.jsx
import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import Loader from '../../components/common/Loader';
import { FaSearch, FaUserCircle } from 'react-icons/fa';

const Authors = () => {
  const [authors, setAuthors] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    const getAuthors = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get(`${process.env.REACT_APP_BASE_URL}/users`);
        setAuthors(response.data);
        setError(null);
      } catch (err) {
        console.error('Error fetching authors:', err);
        setError('Failed to load authors. Please try again later.');
      } finally {
        setIsLoading(false);
      }
    };
    
    getAuthors();
  }, []);

  // Filter authors based on search term
  const filteredAuthors = searchTerm 
    ? authors.filter(author => 
        author.name.toLowerCase().includes(searchTerm.toLowerCase())
      )
    : authors;

  if (isLoading) {
    return (
      <div className="min-h-[300px] flex items-center justify-center py-12">
        <Loader />
      </div>
    );
  }

  return (
    <section className="py-8">
      <div className="container mx-auto px-4">
        <div className="mb-8 text-center">
          <h1 className="text-3xl font-bold text-gray-900 mb-4">
            Discover Authors
          </h1>
          <p className="text-gray-600 max-w-2xl mx-auto">
            Follow your favorite authors to see their posts in your feed and stay updated with their latest content.
          </p>
        </div>
        
        {/* Search bar */}
        <div className="max-w-md mx-auto mb-8">
          <div className="relative">
            <input 
              type="text"
              placeholder="Search authors..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full px-4 py-3 pl-10 border border-gray-300 rounded-full focus:outline-none focus:ring-2 focus:ring-primary focus:border-transparent"
            />
            <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
          </div>
        </div>
        
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded relative mb-6" role="alert">
            <span className="block sm:inline">{error}</span>
          </div>
        )}
        
        {!isLoading && !error && filteredAuthors.length === 0 && (
          <div className="text-center py-12 bg-white rounded-lg shadow-sm">
            {searchTerm ? (
              <>
                <h2 className="text-2xl font-bold text-gray-700 mb-2">No Authors Found</h2>
                <p className="text-gray-500">No authors match your search criteria.</p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-gray-700 mb-2">No Authors Available</h2>
                <p className="text-gray-500">There are no authors registered on the platform yet.</p>
              </>
            )}
          </div>
        )}
        
        {filteredAuthors.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {filteredAuthors.map(({ _id: id, avatar, name, posts }) => (
              <Link 
                key={id} 
                to={`/posts/users/${id}`}
                className="bg-white rounded-lg shadow-sm p-4 flex items-center gap-4 hover:shadow-md transition-shadow"
              >
                <div className="h-16 w-16 rounded-full overflow-hidden bg-gray-100 flex-shrink-0">
                  {avatar ? (
                    <img 
                      src={`${process.env.REACT_APP_ASSETS_URL}/uploads/${avatar}`} 
                      alt={name}
                      className="w-full h-full object-cover"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center bg-gray-200 text-gray-400">
                      <FaUserCircle size={32} />
                    </div>
                  )}
                </div>
                
                <div>
                  <h4 className="font-medium text-gray-900 hover:text-primary transition-colors">
                    {name}
                  </h4>
                  <p className="text-sm text-gray-500 mt-1">
                    {posts} {posts === 1 ? 'post' : 'posts'}
                  </p>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </section>
  );
};

export default Authors;